import React, { useEffect, useMemo } from 'react';

import Tabs from './Tabs';

export default function Fourrage({ trial, mainColor, category }) {
    const [currentDate, setCurrentDate] = React.useState('');
    const [activeProductTitle, setActiveProductTitle] = React.useState('');

    const productList = useMemo(() => {
        return trial?.data?.products || [];
    }, [trial]);

    const activeProductsTitles = useMemo(() => {
        const titles = productList.map((p) => p.product.title);

        return [...new Set(titles)];
    }, [productList]);

    const activeDates = useMemo(() => {
        return productList
            .filter((p) => p.date_label && p.product.title === activeProductTitle)
            .map((p) => p.date_label);
    }, [productList, activeProductTitle]);

    useEffect(() => {
        setActiveProductTitle(activeProductsTitles?.[0]);
    }, [activeProductsTitles]);

    let activeProduct = useMemo(() => {
        return productList.find((p) => {
            if (currentDate) {
                return (
                    p.product.title === activeProductTitle && p.date_label === currentDate
                );
            }
            return p.product.title === activeProductTitle;
        });
    }, [productList, activeProductTitle, currentDate]);

    useEffect(() => {
        if (activeDates.length > 0) {
            setCurrentDate(activeDates[0]);
        }
    }, [activeDates]);

    const handleChangeActiveProduct = (title) => {
        setActiveProductTitle(title);
    };

    const handleDateChange = (value) => {
        setCurrentDate(value);
    };

    if (!activeProduct) return null;

    const extraFields = Object.values(activeProduct['extra-fields'] ?? {});

    const ps = extraFields.filter(
        (extraField) => extraField.field === 'PS_(écart)_LG'
    )[0]?.value

    const protein = extraFields.filter(
        (extraField) => extraField.field === 'TAUX_DE_PROTEINE_(écart)_LG'
    )[0]?.value;

    const caliber = extraFields.filter(
        (extraField) => extraField.field === 'CALIBRAGE_(écart)_LG'
    )[0]?.value

    return (
        <div>
            <section className="-mx-4">
                <section
                    className={`flex flex-col w-full pt-16 text-white ${mainColor} MapOverlay-header`}
                >
                    <h2 className="flex-1 mb-8 text-2xl text-white">{trial.data?.zipcode} - {trial.data?.city?.toUpperCase()}</h2>
                    <div>
                        <Tabs
                            activeProductTitle={activeProductTitle}
                            setActiveProductTitle={handleChangeActiveProduct}
                            productsTitles={activeProductsTitles}
                            trialId={trial.id}
                            mainColor={mainColor}
                        />
                    </div>
                </section>
                <div id="TabContent" className="px-8 py-8 lg:px-20">
                    {/* select date */}
                    {activeDates.length > 0 ? (
                        <div>
                            <select
                                className="mb-10"
                                value={currentDate ?? ''}
                                onChange={(e) => handleDateChange(e.target.value)}
                            >
                                {activeDates &&
                                    activeDates.map((opt) => {
                                        return (
                                            <option key={opt} value={opt}>
                                                {opt}
                                            </option>
                                        );
                                    })}
                            </select>
                        </div>
                    ) : null}

                    {ps || protein || caliber ? (
                        <div className="border-b divide-y mb-14">
                            {ps ? (
                                <div className={`grid grid-cols-2 py-3 font-semibold`}>
                                    <div>PS</div>
                                    <div
                                        className={`text-right`}
                                    >
                                        {ps}
                                    </div>
                                </div>
                            ) : null}

                            {protein ? (
                                <div className={`grid grid-cols-2 py-3 font-semibold`}>
                                    <div>Taux de protéine</div>
                                    <div
                                        className={`text-right`}
                                    >
                                        {protein}
                                    </div>
                                </div>
                            ) : null}

                            {caliber ? (
                                <div className={`grid grid-cols-2 py-3 font-semibold`}>
                                    <div>Calibrage à 2,5 mm</div>
                                    <div
                                        className={`text-right`}
                                    >
                                        {caliber}
                                    </div>
                                </div>
                            ) : null}

                            {activeProduct?.humidity ? (
                                <div className={`grid grid-cols-2 py-3 font-semibold`}>
                                    <div>Humidité (%H2O)</div>
                                    <div
                                        className={`text-right`}
                                    >
                                        {activeProduct.humidity}
                                    </div>
                                    <div className="text-right">
                                        {extraFields.filter(
                                            (extraField) => extraField.field === 'TX_H2O_MOY_ESSAI'
                                        )[0]?.value ?? activeProduct.humidity_mean}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ) :null}

                    <div className="font-semibold border-b divide-y">
                        {activeProduct.ground_type && (
                            <div className="flex justify-between py-3">
                                <div>Type de sol :</div>
                                <div>{activeProduct.ground_type}</div>
                            </div>
                        )}

                        {activeProduct.test_type && (
                            <div className="flex justify-between py-3">
                                <div>Type d'essais :</div>
                                <div className="text-right">{activeProduct.test_type}</div>
                            </div>
                        )}

                        {activeProduct.date_semis && (
                            <div className="flex justify-between py-3">
                                <div>Date de semis :</div>
                                <div className="text-right">{activeProduct.date_semis}</div>
                            </div>
                        )}

                        {activeProduct.date_recolte && (
                            <div className="flex justify-between py-3">
                                <div>Date de récolte :</div>
                                <div className="text-right">{activeProduct.date_recolte}</div>
                            </div>
                        )}

                        {activeProduct.irrigation && (
                            <div className="flex justify-between py-3">
                                <div>Disponibilité en eau :</div>
                                <div className="text-right">{activeProduct.irrigation}</div>
                            </div>
                        )}

                        {extraFields.length > 0 &&
                            extraFields
                                .filter(
                                    (extraField) =>
                                        extraField.field !== 'TX_H2O_MOY_ESSAI' &&
                                        extraField.field !== 'ANNEE' &&
                                        extraField.field !== 'PS_(écart)_LG' &&
                                        extraField.field !== 'NOMBRE_ESSAI_RENDEMENT' &&
                                        extraField.field !== 'PS_(kg/hl)_MOYENNE' &&
                                        extraField.field !== 'NOMBRE_ESSAI_PS' &&
                                        extraField.field !== 'TAUX_DE_PROTEINE_(écart)_LG' &&
                                        extraField.field !== 'TAUX_DE_PROTEINE_(%)_MOYENNE' &&
                                        extraField.field !== 'NOMBRE_ESSAI_TAUX_DE_PROTEINE' &&
                                        extraField.field !== 'CALIBRAGE_(écart)_LG' &&
                                        extraField.field !== 'CALIBRAGE_(%)_MOYENNE' &&
                                        extraField.field !== 'NOMBRE_ESSAI_CALIBRAGE'
                                )
                                ?.map((row, index) => {
                                    if (!row.value) return null;

                                    let value = row.value;
                                    if (typeof value !== 'string' && !Number.isInteger(value)) {
                                        value = parseFloat(row.value).toFixed(2);
                                    }

                                    return (
                                        <div
                                            className="flex justify-between py-3"
                                            key={`${index}-${row.value}`}
                                        >
                                            <div>{row.field} :</div>
                                            <div className="text-right">
                                                {value}
                                            </div>
                                        </div>
                                    );
                                })}
                    </div>
                    {activeProduct.comment_field ? <div dangerouslySetInnerHTML={{__html: activeProduct.comment_field}}/> : null}
                    {activeProduct.img ? <img src={activeProduct.img} alt={""} className="block mt-4 w-full"/> : null}
                </div>
            </section>

            <div className="flex items-center flex-col md:flex-row gap-2 MapOverlay-content">
                { window.PARENT_TRELFE ? (
                    <a href={window.PARENT_TRELFE} className="inline-block btn btn--rounded" target="_blank" rel="noreferrer">
                        Toutes nos compositions prairiales de courte durée
                    </a>
                ): null}
                <a
                    href={activeProduct.product?.url}
                    className="inline-block btn btn--rounded md:ml-auto"
                    target="_blank"
                    rel="noreferrer"
                >
                    Voir la fiche produit {activeProduct.product?.title}
                </a>
            </div>
        </div>
    );
}
